import React, { useState, useRef, useEffect } from 'react';

import { AgGridReact } from 'ag-grid-react'; // AG Grid Component
import "ag-grid-community/styles/ag-grid.css"; // Mandatory CSS required by the grid
import "ag-grid-community/styles/ag-theme-quartz.css"; // Optional Theme applied to the grid
import './EmployeeInformation.css'
import "ag-grid-enterprise";
import { RowGroupingModule } from '@ag-grid-enterprise/row-grouping';
import { ServerSideRowModelModule } from '@ag-grid-enterprise/server-side-row-model';

import { currencyFormatter, dateFormatter } from '../valueformatter';
import AddRowModal from '../addRowModal/AddRowModal';
import { ConfirmDeleteModal } from '../addRowModal/ConfirmDeleteModal';

export default function EmployeeInformation(props) {
  // Row Data: The data to be displayed.
  const [rowData, setRowData] = useState([]);
  
  // Modal state and form fields
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);

  // Preserving Filter Model
  const [filterModel, setFilterModel] = useState(null);  // Save current filters

  // Loading state to track when data is being fetched
  const [loading, setLoading] = useState(true);
  
  const gridOptions= {
    sideBar: true,
    rowGroupPanelShow: 'always',
    // groupDefaultExpanded: 1,
    columnHoverHighlight: true,
    // groupIncludeFooter: true,
    groupIncludeTotalFooter: true,
    suppressAggFuncInHeader: true,
    getRowStyle: params => {
      // Grand Total row formatting
      if (params.node.group && params.node.level === -1) {
          return { backgroundColor: '#3F91B8', color: 'white', fontWeight: 'bold' };
      }
      // Subtotal row formatting
      if (params.node.group) {
          return { backgroundColor: '#b5c4ca', fontWeight: 'bold' };
      }
      // Alternating row colors
      return { backgroundColor: params.node.rowIndex % 2 === 0 ? '#f9f9f9' : '#c3e6f7' };
  },
    autoSizeStrategy: {
      type: 'fitCellContents'
    },
  };

  const gridRef = useRef(); // Optional - for accessing Grid's API

  // DefaultColDef sets props common to all Columns
  const defaultColDef = {
    filter: true,
  };

  // Column Definitions: Defines the columns to be displayed.
  const handleDeleteRow = (id) => {
    setPendingDeleteId(id);
    setShowDeleteModal(true);
  };

  const confirmDeleteRow = async () => {
    if (!pendingDeleteId) return;
    try {
      // Create body
      const body = {
        data: [{ id: pendingDeleteId }],
      };

      // TODO: NEED TO IMPLEMENT AUTH
      // Basic Auth
      // const username = process.env.REACT_APP_MARKETING_ANALYTICS_API_USER;
      // const password = process.env.REACT_APP_MARKETING_ANALYTICS_API_PASS;
      // const credentials = btoa(`${username}:${password}`);

      const reqUrl = `${process.env.REACT_APP_QF_API}/${process.env.REACT_APP_OTG_ID}/data/delete/ORG.EMPLOYEE_INFORMATION/id`; // PROD
      // const reqUrl = `http://localhost:1337/${process.env.REACT_APP_OTG_ID}/data/delete/ORG.EMPLOYEE_INFORMATION/id`; // TEST

      const response = await fetch(reqUrl, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        //   "Authorization": `Basic ${credentials}`
        },
        body: JSON.stringify(body)
      });
      if (!response.ok) {
        throw new Error("Failed to delete record");
      }

			console.log("Record deleted successfully");
			// Remove the deleted row from the grid
      setRowData(prev => prev.filter(row => row.id !== pendingDeleteId));
    } catch (err) {
      alert("Error deleting record: " + err.message);
    } finally {
      setShowDeleteModal(false);
      setPendingDeleteId(null);
    }
  };

  const cancelDeleteRow = () => {
    setShowDeleteModal(false);
    setPendingDeleteId(null);
  };


  // Column Definitions: Defines the columns to be displayed.
  const colDefs = [
    { field: "name", headerName: "Name", enableRowGroup: true, editable: true },
    { field: "team_name", headerName: "Team Name", enableRowGroup: true, editable: true, 
        cellRenderer: (params) => {
      return params.data && params.data.updating
        ? `<div class="cell-spinner">${params.value}</div>`
        : params.value;
    },
    },
    { 
      field: "date", headerName: "Date", enableRowGroup: true, editable: true,
      valueFormatter: params => dateFormatter(params.value),
    },
    { field: "ext", headerName: "Ext", enableRowGroup: true, editable: true, },
    { field: "id", headerName: "id", enableRowGroup: true, hide:true },
    {
      field: 'delete',
      headerName: 'Delete',
      cellRenderer: params => {
        if (params.node.group) {
          return null;
        }
        return <button className="delete-row-btn" onClick={() => handleDeleteRow(params.data.id)} >Delete</button>
      },
      editable: false,
      cellStyle: { textAlign: 'center' }
    }
  ];


  // Fetch data based on date range
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true); // Set loading to true before fetching
      if (gridRef.current) {
        // Save current filter state
        const currentFilterModel = gridRef.current.api.getFilterModel();
        setFilterModel(currentFilterModel);
      }

      const requestOptions = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          replaceKeys: {
            "{{startDate}}": props.startDate,
            "{{endDate}}": props.endDate
          }
        })
      };

      const reqUrl = `${process.env.REACT_APP_QF_API}/${process.env.REACT_APP_OTG_ID}/data/WT7foopUoD04Up1Dugkc`; // PROD
      // const reqUrl = `http://localhost:1337/${process.env.REACT_APP_OTG_ID}/data/M5Ot4OxVVrkUuL5sAg2a`; // TEST

      try {
        const result = await fetch(reqUrl, requestOptions);
        const respData = await result.json();
        // Check for error 
        if(respData.status !== "success") {
          alert("Error fetching data: " + respData.msg);
          setRowData([]);
          return;
        }

        // Add updating flag to each row
        const enrichedData = respData.data.map(row => ({ ...row, updating: false }));
        setRowData(enrichedData);
        
        // setRowData(respData.data);
      } catch (error) {
        console.error("ERROR: " + error);
      } finally {
        setLoading(false); // Set loading to false after fetching
      }
    };

    fetchData();
  }, [props.reportName, props.startDate, props.endDate]);

  // After new data is set, reapply the filter model
  useEffect(() => {
    if (gridRef.current && filterModel) {
      gridRef.current.api.setFilterModel(filterModel);
    }
  }, [rowData, filterModel]);


  // Function to handle updating employee and send POST request to update in the DB
  const handleCellValueChanged = async (params) => {
    if (params.oldValue !== params.newValue) {
        console.log("new value inputted params: ", params.colDef.field);
        const fieldName = params.colDef.field;

        let dataMap = {};
        // Add fieldName (column name in database) and new value
        dataMap[fieldName] = params.newValue;
        dataMap["id"] = params.data.id;

        console.log("dataMap: ", dataMap);
        // Set updating flag
        // params.node.setDataValue("updating", true);
        try {
            // Create body
            const body = {
                data: [dataMap],
            };
            
            /*
            // TODO: NEED TO IMPLEMENT AUTH
            // Basic Auth
            const username = process.env.REACT_APP_MARKETING_ANALYTICS_API_USER;
            const password = process.env.REACT_APP_MARKETING_ANALYTICS_API_PASS;

            // Encode to Base64
            const credentials = btoa(`${username}:${password}`);
            */

            const apiURL = `${process.env.REACT_APP_QF_API}/${process.env.REACT_APP_OTG_ID}/data/upsert/ORG.EMPLOYEE_INFORMATION/id` // PROD
            // const apiURL = `http://localhost:1337/${process.env.REACT_APP_OTG_ID}/data/upsert/ORG.EMPLOYEE_INFORMATION/id` // TEST

            const response = await fetch(apiURL, {
                method: "POST",
                headers: { 
                    "Content-Type": "application/json",
                    // "Authorization": `Basic ${credentials}`
                },
                body: JSON.stringify(body)
            });

            if (!response.ok) {
                throw new Error("Failed to update record");
            }

            console.log("Record updated successfully");
        } catch (err) {
            console.error(err);
            // revert change in grid if error
            // params.node.setDataValue("team", params.oldValue);
        } finally {
            // Clear updating flag
            // params.node.setDataValue("updating", false);
        }
    }
  };

  // Handler to add a new row
  const handleSaveRow = async (params) => {
    const dataMap = {
      ...params,
      name: params.name,
      team_name: params.team_name,
      date: params.date,
      ext: params.ext,
      id: `${params.name.toLowerCase().replace(/\s+/g, '')}_${params.date.replace(/-/g, '')}` // Unique ID based on name lowercased and concatenated (no spaces) and date. e.g: douglasfalcon_20250904
    };

	  console.log("dataMap to be added: ", dataMap);

    // Only update grid state when a row is added
    if (rowData.some(row => row.id === dataMap.id)) {
      alert("Error: A record with this Date and Name already exists.");
      return false; // Indicate failure
    }

    // Send POST request to add the new row to the database
    try {
			// Create body
      const body = { data: [dataMap] };

      /* TODO: NEED TO IMPLEMENT AUTH
      // Basic Auth
      const username = process.env.REACT_APP_MARKETING_ANALYTICS_API_USER;
      const password = process.env.REACT_APP_MARKETING_ANALYTICS_API_PASS;

      // Encode to Base64
      const credentials = btoa(`${username}:${password}`);
      */

      const apiURL = `${process.env.REACT_APP_QF_API}/${process.env.REACT_APP_OTG_ID}/data/upsert/ORG.EMPLOYEE_INFORMATION/id`; // PROD
      // const apiURL = `http://localhost:1337/${process.env.REACT_APP_OTG_ID}/data/upsert/ORG.EMPLOYEE_INFORMATION/id`; // TEST

      const response = await fetch(apiURL, {
        method: "POST",
        headers: { 
                "Content-Type": "application/json",
                // "Authorization": `Basic ${credentials}`
        },
        body: JSON.stringify(body)
      });

      if (!response.ok) {
              throw new Error("Failed to add record");
      }

      console.log("Record added successfully");
      let id = dataMap.id;
			
			// Add new row to the grid
      setRowData(prev => [...prev, { ...dataMap, id }]);
      setShowModal(false);
      return true; // Indicate success
    } catch (err) {
      alert("Error adding record: " + err.message);
      return false; // Indicate failure
    }
  };

  // Autosize the grouped column when the grid is first rendered
  const onFirstDataRendered = (params) => {
    setTimeout(() => {
      params.columnApi.autoSizeAllColumns();
    }, 100);
  };

  // Autosizes all columns when row group is expanded
  const onRowGroupOpened = (params) => {
    setTimeout(() => {
      params.columnApi.autoSizeAllColumns();
    }, 100);
  };

  // Autosizes all columns when row group is expanded
  const onColumnRowGroupChanged = (params) => {
    setTimeout(() => {
      params.columnApi.autoSizeAllColumns();
      // Get the row group columns
      const rowGroupCols = params.columnApi.getRowGroupColumns();
      if (rowGroupCols.length > 0) {
        // Get the last added group column
        const lastGroupCol = rowGroupCols[rowGroupCols.length - 1];
        // Apply sort to the grouped column
        params.columnApi.applyColumnState({
          state: [{ colId: lastGroupCol.getColId(), sort: 'asc' }]
        });
      }
    }, 100);
  };

  return (
    // wrapping container with theme & size
    <div
      className="ag-theme-quartz h-78 greyBg" // applying the grid theme
    > 
      <div id="wrapper">
        <div id="summaryBox">
          <p id="summaryText">
            <strong>Employee Information</strong><br />
            This view will the employee's name and the team they belonged to for a specific day. The team name field is editable.
          </p>
        </div>
        <button className='greenButton m-bot-3' onClick={() => setShowModal(true)}>Add Row</button>
        <AddRowModal isOpen={showModal} onClose={() => setShowModal(false)} onSaveRow={handleSaveRow} />
      </div>
      <div style={{ position: 'relative', height: '85%' }}> {/* New wrapper for AG Grid */}
        <AgGridReact
          onFirstDataRendered={onFirstDataRendered}
          onRowGroupOpened={onRowGroupOpened}
          onColumnRowGroupChanged={onColumnRowGroupChanged}
          onCellValueChanged={handleCellValueChanged}
          enableCharts={true}
          enableRangeSelection={true}
          rowData={rowData}
          columnDefs={colDefs}
          // getGroupRowAgg={getGroupRowAgg}
          gridOptions={gridOptions}
          defaultColDef={defaultColDef}
          modules={[ServerSideRowModelModule, RowGroupingModule]}
          defaultSideBar={false}
          sideBar={{
            toolPanels: [
              {
                id: 'columns',
                labelDefault: 'Columns',
                labelKey: 'columns',
                iconKey: 'columns',
                toolPanel: 'agColumnsToolPanel',
              },
              {
                id: 'filters',
                labelDefault: 'Filters',
                labelKey: 'filters',
                iconKey: 'filter',
                toolPanel: 'agFiltersToolPanel',
              }
            ]
          }}
        />
        {loading && (
          <div className="loading-overlay">
            <div className="spinner"></div>
          </div>
        )}
        <ConfirmDeleteModal
          isOpen={showDeleteModal}
          onConfirm={confirmDeleteRow}
          onCancel={cancelDeleteRow}
        />
      </div>
    </div>
  )
}